import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH, N1_TEXTURE_KEY } from '../config';
import { tierOfLabel, type N1Manifest, type N1TierLabel } from '../assets/n1Manifest';
import type { RegisteredAction } from '../animation/registerN1Animations';
import { Sfx } from '../audio/sfx';
import { runOptionsFromUrl } from '../scenes/BootScene';
import { publishSnapshot, pushHistory, recordError, snapshot } from '../debug/snapshot';
import { EdgeKeys, KeyboardControls } from '../input/controls';
import { EdgeCounter } from '../input/edgeCounter';
import { mergeInputs } from '../input/merge';
import { TouchPad } from '../input/touch';
import { buildActionGraph, type ActionGraph } from '../player/actionGraph';
import { GeorgiPlayer } from '../player/GeorgiPlayer';
import { PLAYER_TUNING, validateTuning } from '../player/tuning';
import {
  ITEM_TEXTURES,
  PARALLAX,
  ensureItemTextures,
  ensureThemeTextures,
  themeKeys,
} from '../world/themeTextures';
import { dailyLabel, dailySeed } from './daily';
import {
  CHUNK_W,
  GROUND_MAX_Y,
  WORLD_H,
  chunkProblems,
  generateChunk,
  ICE_ACCEL_SCALE,
  type Chunk,
  type GenCreature,
  type GenPlatform,
  type GenRoller,
  type PickupKind,
} from './generator';
import { levelAt, levelByIndex, levelIndexAt, type LevelDef } from './levels';
import {
  POWERUPS,
  absorbHit,
  activate,
  activeList,
  airJumpsAvailable,
  createPowerups,
  isActive,
  landed,
  magnetRadiusPx,
  speedScale,
  tickPowerups,
  useAirJump,
  type PowerupKind,
  type PowerupState,
} from './powerups';
import {
  SCORING,
  betterOf,
  collect,
  completeLevel,
  createScore,
  distanceM,
  isGameOver,
  loseLife,
  parseRecord,
  stomp,
  tickScore,
  toRecord,
  type BestRecord,
  type ScoreState,
} from './scoring';

export interface EndlessSceneData {
  manifest: N1Manifest;
  tierLabel: N1TierLabel;
  registered: RegisteredAction[];
  /** Run seed; a new one is drawn per restart unless the URL pins it. */
  seed?: number;
  /** Starting lives (tests use 1). */
  lives?: number;
  /** Skip the title screen and start running immediately. */
  autoStart?: boolean;
  /** Show the on-screen touch pad (auto on touch devices). */
  touch?: boolean;
  /** This run uses the shared daily seed. */
  daily?: boolean;
  /** Runtime in-betweening on/off (default: tuning). */
  blend?: boolean;
}

export type RunMode = 'title' | 'running' | 'paused' | 'gameover';

const DEPTH = {
  sky: -10,
  ground: 10,
  hazard: 12,
  item: 14,
  flag: 13,
  fx: 18,
  player: 20,
  bubble: 22,
  debug: 50,
  hud: 100,
  overlay: 110,
} as const;
const MAX_DT_MS = 50;
const AHEAD_PX = GAME_WIDTH * 2;
const BEHIND_CHUNKS = 2;
const RESPAWN_BELOW_Y = WORLD_H + 120;
const INVULNERABLE_MS = 1500;
const CRUMBLE_DELAY_MS = 500;
const BEST_KEY = 'georgi-go:best';
const HUD_FONT = "'Trebuchet MS', 'Segoe UI', system-ui, sans-serif";

const HELP_TEXT = [
  'A/D or ←/→ move · hold Shift to run · Space/W/↑ jump',
  'P pause · M mute · B overlay · H help · G gallery',
].join('\n');

interface LiveChunk {
  chunk: Chunk;
  objects: Phaser.GameObjects.GameObject[];
}

interface Mover {
  sprite: Phaser.GameObjects.TileSprite;
  platform: GenPlatform;
  lastX: number;
  lastY: number;
}

interface PickupObject extends Phaser.GameObjects.Image {
  pickupKind?: PickupKind;
}

interface Zone extends Phaser.GameObjects.Zone {
  platformData?: GenPlatform;
  crumbling?: boolean;
}

interface RollerObject extends Phaser.GameObjects.Image {
  rollerData?: GenRoller;
  awake?: boolean;
}

interface CreatureObject extends Phaser.GameObjects.Image {
  creatureData?: GenCreature;
  awake?: boolean;
  /** rock: triggered and falling. */
  falling?: boolean;
  /** beetle: current walking direction. */
  dir?: 1 | -1;
  /** rock: its ground marker. */
  marker?: Phaser.GameObjects.Image;
}

type HitReason = 'spikes' | 'pit' | 'roller' | 'beetle' | 'bat' | 'rock';

interface DeathStats {
  byReason: Record<string, number>;
  byPattern: Record<string, number>;
}

const STATS_KEY = 'georgi-go:stats';
/** Georgi's feet sit this far below the view centre (and this far right of it is the lookahead). */
const CAMERA_OFFSET = { x: -180, y: 144 } as const;
const SWIPE_JUMP = { minDy: 60, maxMs: 450 } as const;

const ROLLER_WAKE_PX = 1400;
const STOMP_BOUNCE = 460;

/**
 * The endless run: procedurally generated chunks streamed ahead of Georgi, levels with their own
 * themes and hazards, bones and power-ups, lives with a safe-spot respawn, score with combos,
 * a persisted best, title / pause / game-over screens and the same debug overlay as the room.
 */
export class EndlessScene extends Phaser.Scene {
  private manifest!: N1Manifest;
  private tierLabel!: N1TierLabel;
  private registered: RegisteredAction[] = [];
  private graph!: ActionGraph;
  private seed = 1;
  private autoStart = false;
  private daily = false;
  private blend: boolean | undefined;
  private forceTouch = false;
  private touch: TouchPad | null = null;
  private tapStart = false;
  private swipeStart: { x: number; y: number; t: number } | null = null;
  private readonly swipeJumps = new EdgeCounter();
  private deaths: DeathStats = { byReason: {}, byPattern: {} };
  private mode: RunMode = 'title';
  private score: ScoreState = createScore();
  private powerups: PowerupState = createPowerups();
  private best: BestRecord | null = null;

  private player!: GeorgiPlayer;
  private controls: KeyboardControls | null = null;
  private commands: EdgeKeys<
    'start' | 'pause' | 'mute' | 'overlay' | 'help' | 'gallery' | 'daily'
  > | null = null;
  private readonly sfx = new Sfx();

  private platforms!: Phaser.Physics.Arcade.StaticGroup;
  private crumbles!: Phaser.Physics.Arcade.StaticGroup;
  private hazards!: Phaser.Physics.Arcade.StaticGroup;
  private springs!: Phaser.Physics.Arcade.StaticGroup;
  private pickups!: Phaser.Physics.Arcade.Group;
  private moverBodies!: Phaser.Physics.Arcade.Group;
  private rollers!: Phaser.Physics.Arcade.Group;
  private creatures!: Phaser.Physics.Arcade.Group;
  private windZones!: Phaser.Physics.Arcade.StaticGroup;
  private windThisFrame = 0;
  private movers: Mover[] = [];
  private live = new Map<number, LiveChunk>();
  private nextChunk = 0;
  private lastExitGroundY = GROUND_MAX_Y;
  private minChunkX = 0;

  private safeSpot = { x: 200, y: GROUND_MAX_Y };
  private groundedPlatform: GenPlatform | null = null;
  private invulnerableUntil = 0;
  private elapsedMs = 0;
  private respawns = 0;
  private levelIndex = 0;
  private sky!: Phaser.GameObjects.TileSprite;
  private skyNext: Phaser.GameObjects.TileSprite | null = null;
  private hills!: Phaser.GameObjects.TileSprite;
  private clouds!: Phaser.GameObjects.TileSprite;
  private bubble!: Phaser.GameObjects.Image;
  private dust!: Phaser.GameObjects.Particles.ParticleEmitter;

  private hud!: {
    score: Phaser.GameObjects.Text;
    combo: Phaser.GameObjects.Text;
    level: Phaser.GameObjects.Text;
    distance: Phaser.GameObjects.Text;
    hearts: Phaser.GameObjects.Image[];
    best: Phaser.GameObjects.Text;
    powerups: Phaser.GameObjects.Text;
    help: Phaser.GameObjects.Text;
    banner: Phaser.GameObjects.Text;
    bannerSub: Phaser.GameObjects.Text;
    overlayText: Phaser.GameObjects.Text;
  };
  private screen: Phaser.GameObjects.Container | null = null;
  private debugGraphics!: Phaser.GameObjects.Graphics;
  private overlayVisible = false;
  private helpVisible = true;
  private failed = false;

  constructor() {
    super('Endless');
  }

  init(data: EndlessSceneData): void {
    this.manifest = data.manifest;
    this.tierLabel = data.tierLabel;
    this.registered = data.registered;
    this.seed = data.seed ?? Date.now() % 1_000_000_007;
    this.autoStart = data.autoStart ?? false;
    this.daily = data.daily ?? false;
    this.blend = data.blend;
    this.forceTouch = data.touch ?? false;
    this.touch = null;
    this.tapStart = false;
    this.swipeStart = null;
    this.swipeJumps.reset();
    this.windThisFrame = 0;
    this.deaths = this.loadStats();
    this.score = createScore(data.lives ?? SCORING.startLives, 200);
    this.powerups = createPowerups();
    this.mode = 'title';
    this.movers = [];
    this.live = new Map();
    this.nextChunk = 0;
    this.lastExitGroundY = GROUND_MAX_Y;
    this.minChunkX = 0;
    this.safeSpot = { x: 200, y: GROUND_MAX_Y };
    this.groundedPlatform = null;
    this.invulnerableUntil = 0;
    this.elapsedMs = 0;
    this.respawns = 0;
    this.levelIndex = 0;
    this.skyNext = null;
    this.screen = null;
    this.overlayVisible = false;
    this.helpVisible = true;
    this.failed = false;
    this.best = this.loadBest();
  }

  create(): void {
    const problems = validateTuning(PLAYER_TUNING).map((p) => `tuning: ${p}`);
    if (problems.length) {
      this.fail(problems.join('\n'));
      return;
    }
    try {
      this.graph = buildActionGraph(
        this.manifest,
        tierOfLabel(this.tierLabel).tier,
        PLAYER_TUNING.jumpEntryLiftPx,
      );
    } catch (error) {
      this.fail(error instanceof Error ? error.message : String(error));
      return;
    }

    ensureItemTextures(this);
    const level = levelByIndex(0);
    ensureThemeTextures(this, level.theme, GAME_HEIGHT);
    this.cameras.main.setBackgroundColor(level.theme.sky[1]);
    this.sky = this.add
      .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, themeKeys(level.theme).sky)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.sky);
    const keys0 = themeKeys(level.theme);
    this.clouds = this.add
      .tileSprite(0, 40, GAME_WIDTH, PARALLAX.cloudsH, keys0.clouds)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.sky + 2);
    // Hills rest on the base ground line in world y (they scroll vertically with the camera, so no
    // sky gap opens while it follows a jump) and parallax horizontally through their tile offset.
    this.hills = this.add
      .tileSprite(0, GROUND_MAX_Y - PARALLAX.hillsH, GAME_WIDTH, PARALLAX.hillsH, keys0.hills)
      .setOrigin(0, 0)
      .setScrollFactor(0, 1)
      .setDepth(DEPTH.sky + 3);

    const world = this.physics.world;
    world.setBoundsCollision(true, true, false, false);
    world.gravity.y = PLAYER_TUNING.gravity;

    this.platforms = this.physics.add.staticGroup();
    this.crumbles = this.physics.add.staticGroup();
    this.hazards = this.physics.add.staticGroup();
    this.springs = this.physics.add.staticGroup();
    this.pickups = this.physics.add.group({ allowGravity: false, immovable: true });
    this.moverBodies = this.physics.add.group({ allowGravity: false, immovable: true });
    this.rollers = this.physics.add.group({ allowGravity: true });
    this.creatures = this.physics.add.group({ allowGravity: false });
    this.windZones = this.physics.add.staticGroup();

    this.player = new GeorgiPlayer(
      this,
      this.manifest,
      this.tierLabel,
      this.graph,
      PLAYER_TUNING,
      { x: this.safeSpot.x, feetY: this.safeSpot.y, facing: 'right' },
      { frameBlend: this.blend },
    );
    this.player.sprite.setDepth(DEPTH.player);
    this.ensureChunks();

    this.physics.add.collider(this.player.sprite, this.platforms, (_p, zone) =>
      this.touchPlatform(zone as Zone),
    );
    this.physics.add.collider(this.player.sprite, this.crumbles, (_p, zone) =>
      this.touchCrumble(zone as Zone),
    );
    this.physics.add.collider(this.player.sprite, this.moverBodies, (_p, mover) =>
      this.touchMover(mover as Phaser.GameObjects.TileSprite),
    );
    this.physics.add.overlap(this.player.sprite, this.hazards, () => this.hit('spikes'));
    this.physics.add.overlap(this.player.sprite, this.springs, (_p, zone) =>
      this.touchSpring(zone as Phaser.GameObjects.Zone),
    );
    this.physics.add.overlap(this.player.sprite, this.pickups, (_p, item) =>
      this.collectPickup(item as PickupObject),
    );
    this.physics.add.collider(this.rollers, this.platforms);
    this.physics.add.overlap(this.player.sprite, this.rollers, (_p, roller) =>
      this.touchRoller(roller as RollerObject),
    );
    this.physics.add.collider(this.creatures, this.platforms, (creature) =>
      this.creatureLanded(creature as CreatureObject),
    );
    this.physics.add.overlap(this.player.sprite, this.creatures, (_p, creature) =>
      this.touchCreature(creature as CreatureObject),
    );
    this.physics.add.overlap(this.player.sprite, this.windZones, (_p, zone) => {
      this.windThisFrame = (zone as Phaser.GameObjects.Zone).getData('force') as number;
    });

    this.bubble = this.add
      .image(0, 0, ITEM_TEXTURES.bubble)
      .setDepth(DEPTH.bubble)
      .setVisible(false)
      .setAlpha(0.85);
    this.dust = this.add
      .particles(0, 0, ITEM_TEXTURES.dust, {
        frequency: -1,
        speed: { min: 40, max: 120 },
        angle: { min: 200, max: 340 },
        lifespan: { min: 220, max: 420 },
        scale: { start: 0.7, end: 0.05 },
        alpha: { start: 0.6, end: 0 },
        gravityY: 260,
        tint: 0xf7f1e3,
      })
      .setDepth(DEPTH.fx);

    const camera = this.cameras.main;
    camera.startFollow(this.player.sprite, true, 0.1, 0.1, CAMERA_OFFSET.x, CAMERA_OFFSET.y);
    this.updateBounds();
    this.snapCamera();

    const keyboard = this.input.keyboard;
    if (keyboard) {
      this.controls = new KeyboardControls(keyboard);
      const K = Phaser.Input.Keyboard.KeyCodes;
      this.commands = new EdgeKeys(keyboard, {
        start: K.ENTER,
        pause: K.P,
        mute: K.M,
        overlay: K.B,
        help: K.H,
        gallery: K.G,
        daily: K.D,
      });
    }
    if (this.forceTouch || this.sys.game.device.input.touch) {
      this.touch = new TouchPad(this, GAME_WIDTH, GAME_HEIGHT, DEPTH.hud + 2);
    }
    // A tap anywhere starts (title) or restarts (game over), like Enter; a quick upward swipe
    // is a jump (touch play without hunting for the button).
    this.input.on(Phaser.Input.Events.POINTER_DOWN, (pointer: Phaser.Input.Pointer) => {
      this.tapStart = true;
      this.swipeStart = { x: pointer.x, y: pointer.y, t: pointer.downTime };
    });
    this.input.on(Phaser.Input.Events.POINTER_UP, (pointer: Phaser.Input.Pointer) => {
      const start = this.swipeStart;
      this.swipeStart = null;
      if (!start) return;
      const dy = pointer.y - start.y;
      const ms = pointer.upTime - start.t;
      if (
        -dy >= SWIPE_JUMP.minDy &&
        ms <= SWIPE_JUMP.maxMs &&
        Math.abs(dy) > Math.abs(pointer.x - start.x)
      )
        this.swipeJumps.record({ t: pointer.upTime, id: pointer.id });
    });
    this.buildHud();
    this.debugGraphics = this.add.graphics().setDepth(DEPTH.debug);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.controls?.release();
      this.touch?.releaseAll();
    });

    snapshot.phase = 'endless';
    snapshot.ready = true;
    snapshot.renderer = this.game.renderer.type === Phaser.WEBGL ? 'webgl' : 'canvas';
    snapshot.registered = this.registered.length;
    snapshot.stateHistory = [];
    snapshot.actionHistory = [];
    snapshot.overlay = false;
    snapshot.help = true;
    snapshot.room = null;
    const texture = this.textures.get(N1_TEXTURE_KEY);
    snapshot.actions = this.registered.map((r) => ({
      id: r.id,
      frames: r.frames,
      textureHas: texture.has(`${r.id}/00`),
      playing: false,
    }));
    if (this.autoStart) this.startRun();
    else this.showTitle();
    this.publish();
    publishSnapshot();
  }

  // ---------------------------------------------------------------- run modes

  private startRun(): void {
    this.mode = 'running';
    this.sfx.unlock();
    this.sfx.play('start');
    this.hideScreen();
    this.physics.resume();
  }

  private showTitle(): void {
    this.mode = 'title';
    const best = this.best
      ? `best  ${this.best.score} pts · ${this.best.distanceM} m · level ${this.best.level}`
      : 'no best yet';
    this.showScreen(
      'GEORGI GO',
      this.daily ? `daily run ${dailyLabel(this.seed)}` : 'endless run',
      [
        'Run as far as you can. Bones score, combos multiply, power-ups help.',
        'Pits, spikes and rolling thorns cost a life (stomp the thorns!);',
        'each level cleared gives one back.',
        '',
        best,
        this.deadliestLine(),
        'Enter, Space or tap to start · D for the daily run',
      ],
    );
  }

  private deadliestLine(): string {
    const entries = Object.entries(this.deaths.byReason).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return '';
    return `deadliest so far: ${entries
      .slice(0, 3)
      .map(([k, n]) => `${k} ×${n}`)
      .join(' · ')}`;
  }

  private togglePause(): void {
    if (this.mode === 'running') {
      this.mode = 'paused';
      this.physics.pause();
      this.tweens.pauseAll();
      this.controls?.release();
      this.showScreen('PAUSED', levelAt(this.player.x).name, ['P to resume · G gallery']);
    } else if (this.mode === 'paused') {
      this.mode = 'running';
      this.physics.resume();
      this.tweens.resumeAll();
      this.hideScreen();
    }
  }

  private gameOver(): void {
    this.mode = 'gameover';
    this.physics.pause();
    this.controls?.release();
    this.sfx.play('gameOver');
    const record = toRecord(this.score, this.levelIndex);
    const improved = this.best === null || betterOf(this.best, record) === record;
    this.best = betterOf(this.best, record);
    this.saveBest();
    this.showScreen('GAME OVER', improved ? 'new best!' : levelByIndex(this.levelIndex).name, [
      `${this.score.score} pts · ${distanceM(this.score)} m · level ${this.levelIndex + 1} · ${this.score.bones} bones`,
      this.best
        ? `best  ${this.best.score} pts · ${this.best.distanceM} m · level ${this.best.level}`
        : '',
      '',
      'Enter, Space or tap to run again · D daily run · G gallery',
    ]);
  }

  private restart(daily = this.daily): void {
    this.controls?.release();
    this.touch?.releaseAll();
    const options = runOptionsFromUrl();
    const seed = daily ? dailySeed() : (options.seed ?? (this.seed * 7919 + 13) % 1_000_000_007);
    this.scene.restart({
      manifest: this.manifest,
      tierLabel: this.tierLabel,
      registered: this.registered,
      seed,
      lives: options.lives,
      autoStart: true,
      touch: this.forceTouch,
      daily,
      blend: this.blend,
    } satisfies EndlessSceneData);
  }

  // ------------------------------------------------------------------- update

  override update(_time: number, delta: number): void {
    if (this.failed) return;
    snapshot.renderedFrames += 1;
    const dt = Math.min(delta, MAX_DT_MS);
    const commands = this.commands?.pressed();
    const swipe = this.swipeJumps.consume() > 0;
    const jumpEdge = mergeInputs(this.controls?.snapshot(), this.touch?.snapshot(), {
      moveAxis: 0,
      runHeld: false,
      jumpPressed: swipe,
    });
    const tapped = this.tapStart;
    this.tapStart = false;
    const pauseTapped = this.touch?.consumePause() ?? false;

    if (commands?.has('gallery')) {
      this.toGallery();
      return;
    }
    if (commands?.has('overlay')) this.setOverlay(!this.overlayVisible);
    if (commands?.has('help')) this.setHelp(!this.helpVisible);
    if (commands?.has('mute')) this.sfx.setMuted(!this.sfx.muted);

    if (this.mode === 'title') {
      if (commands?.has('daily')) this.restart(true);
      else if (commands?.has('start') || jumpEdge.jumpPressed || tapped) this.startRun();
      this.player.update(dt, { moveAxis: 0, runHeld: false, jumpPressed: false });
      this.publish();
      return;
    }
    if (this.mode === 'gameover') {
      if (commands?.has('daily')) this.restart(true);
      else if (commands?.has('start') || jumpEdge.jumpPressed || tapped) this.restart();
      this.publish();
      return;
    }
    if (commands?.has('pause') || pauseTapped) this.togglePause();
    if (this.mode === 'paused') {
      this.publish();
      return;
    }

    this.elapsedMs += dt;
    this.powerups = tickPowerups(this.powerups, dt);
    this.updateMovers();

    const onIce = this.groundedPlatform?.kind === 'ice';
    const wind = this.windThisFrame;
    const result = this.player.update(dt, jumpEdge, {
      airJumps: airJumpsAvailable(this.powerups),
      speedScale: speedScale(this.powerups),
      accelScale: onIce ? ICE_ACCEL_SCALE : 1,
    });
    if (wind !== 0) this.player.body.velocity.x += (wind * dt) / 1000;
    for (const e of result.effects) {
      if (e.kind === 'jump') {
        if (e.airborne) {
          this.powerups = useAirJump(this.powerups);
          this.sfx.play('airJump');
        } else this.sfx.play('jump');
      } else if (e.kind === 'land') {
        this.powerups = landed(this.powerups);
        this.dust.explode(6, this.player.x, this.player.feetY - 2);
        this.sfx.play('land');
        if (e.impactVy > 950) this.cameras.main.shake(90, 0.004);
      }
    }

    this.score = tickScore(this.score, dt, this.player.x);
    this.applyMagnet(dt);
    this.updateRollers();
    this.updateCreatures();
    this.checkLevel();
    this.ensureChunks();
    if (this.player.feetY > RESPAWN_BELOW_Y) this.hit('pit');

    const invulnerable = this.elapsedMs < this.invulnerableUntil;
    this.player.sprite.setAlpha(
      invulnerable ? (Math.floor(this.elapsedMs / 80) % 2 ? 0.35 : 1) : 1,
    );
    this.bubble.setVisible(isActive(this.powerups, 'shield'));
    this.bubble.setPosition(this.player.x, this.player.feetY - 60);
    const scrollX = this.cameras.main.scrollX;
    this.sky.tilePositionX = scrollX * 0.15;
    if (this.skyNext) this.skyNext.tilePositionX = this.sky.tilePositionX;
    this.clouds.tilePositionX = scrollX * 0.1 + this.elapsedMs * 0.01;
    this.hills.tilePositionX = scrollX * 0.35;

    this.updateHud();
    this.publish();
    this.drawOverlay();
    // Colliders run in the physics step before update(); clear for the next frame's contacts.
    this.groundedPlatform = null;
    this.windThisFrame = 0;
  }

  private snapCamera(): void {
    this.cameras.main.centerOn(
      this.player.x - CAMERA_OFFSET.x,
      this.player.feetY - CAMERA_OFFSET.y,
    );
  }

  // ------------------------------------------------------------------ chunks

  /**
   * Debug/test helper: puts Georgi on the entry ground of the chunk containing `x`, generating
   * the world up to there first (a body reset outside the current world bounds would be clamped
   * back by Arcade). Not bound to a key.
   */
  teleport(x: number): void {
    const target = Math.max(0, Math.floor(x / CHUNK_W));
    while (this.nextChunk <= target + 2) this.spawnChunk(this.nextChunk);
    const ground = this.live.get(target)?.chunk.entryGroundY ?? GROUND_MAX_Y;
    this.player.respawn({ x, feetY: ground, facing: 'right' });
    this.safeSpot = { x, y: ground };
    this.ensureChunks();
    this.snapCamera();
  }

  private ensureChunks(): void {
    const px = this.player.x;
    while (this.nextChunk * CHUNK_W < px + AHEAD_PX) this.spawnChunk(this.nextChunk);
    for (const [index, live] of this.live) {
      if (live.chunk.x1 < px - BEHIND_CHUNKS * CHUNK_W) {
        for (const o of live.objects) o.destroy();
        this.movers = this.movers.filter((m) => m.sprite.active);
        this.live.delete(index);
      }
    }
    const oldest = Math.min(...this.live.keys());
    const minX = Number.isFinite(oldest) ? oldest * CHUNK_W : 0;
    if (minX !== this.minChunkX) {
      this.minChunkX = minX;
      this.updateBounds();
    }
  }

  private updateBounds(): void {
    const maxX = this.nextChunk * CHUNK_W;
    this.physics.world.setBounds(this.minChunkX, 0, maxX - this.minChunkX, WORLD_H);
    this.cameras.main.setBounds(this.minChunkX, 0, maxX - this.minChunkX, WORLD_H);
  }

  private spawnChunk(index: number): void {
    const chunk = generateChunk(this.seed, index, this.lastExitGroundY, PLAYER_TUNING);
    const prev = this.live.get(index - 1)?.chunk ?? null;
    const problems = chunkProblems(chunk, prev, PLAYER_TUNING);
    if (problems.length) {
      recordError(`chunk ${index} (${chunk.pattern}): ${problems.join('; ')}`);
      console.error(`[georgi] chunk ${index} rejected: ${problems.join('; ')}`);
    }
    this.lastExitGroundY = chunk.exitGroundY;
    this.nextChunk = index + 1;
    const theme = levelAt(chunk.x0).theme;
    ensureThemeTextures(this, theme, GAME_HEIGHT);
    const keys = themeKeys(theme);
    const objects: Phaser.GameObjects.GameObject[] = [];

    for (const p of chunk.platforms) {
      let sprite: Phaser.GameObjects.TileSprite;
      if (p.kind === 'ground' || p.kind === 'ice') {
        // Tall blocks: one cap strip (grass or ice) over tiled soil, so the grass never repeats.
        const capKey = p.kind === 'ice' ? ITEM_TEXTURES.ice : keys.cap;
        const capH = 24;
        sprite = this.add
          .tileSprite(p.x + p.w / 2, p.y + capH + (p.h - capH) / 2, p.w, p.h - capH, keys.soil)
          .setDepth(DEPTH.ground);
        const cap = this.add
          .tileSprite(p.x + p.w / 2, p.y + capH / 2, p.w, capH, capKey)
          .setDepth(DEPTH.ground + 1);
        objects.push(cap);
      } else {
        const key =
          p.kind === 'solid' ? keys.stone : p.kind === 'crumble' ? keys.crumble : keys.plank;
        sprite = this.add
          .tileSprite(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h, key)
          .setDepth(DEPTH.ground);
      }
      objects.push(sprite);
      if (p.kind === 'moving') {
        this.physics.add.existing(sprite, false);
        const body = sprite.body as Phaser.Physics.Arcade.Body;
        body.setAllowGravity(false);
        body.setImmovable(true);
        body.checkCollision.down = false;
        body.checkCollision.left = false;
        body.checkCollision.right = false;
        this.moverBodies.add(sprite);
        this.movers.push({ sprite, platform: p, lastX: sprite.x, lastY: sprite.y });
        continue;
      }
      const zone = this.add.zone(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h) as Zone;
      zone.platformData = p;
      (p.kind === 'crumble' ? this.crumbles : this.platforms).add(zone);
      const body = zone.body as Phaser.Physics.Arcade.StaticBody;
      body.updateFromGameObject();
      if (p.kind === 'one-way' || p.kind === 'crumble') {
        body.checkCollision.down = false;
        body.checkCollision.left = false;
        body.checkCollision.right = false;
      }
      if (p.kind === 'crumble') zone.setData('sprite', sprite);
      objects.push(zone);
    }
    for (const h of chunk.hazards) {
      const sprite = this.add
        .tileSprite(h.x + h.w / 2, h.y + h.h / 2, h.w, h.h, ITEM_TEXTURES.spikes)
        .setDepth(DEPTH.hazard);
      // The deadly zone is a little smaller than the drawing so grazing the edge is forgiven.
      const zone = this.add.zone(h.x + h.w / 2, h.y + h.h / 2 + 4, Math.max(8, h.w - 16), h.h - 8);
      this.hazards.add(zone);
      (zone.body as Phaser.Physics.Arcade.StaticBody).updateFromGameObject();
      objects.push(sprite, zone);
    }
    for (const s of chunk.springs) {
      const sprite = this.add
        .image(s.x + s.w / 2, s.y + s.h, ITEM_TEXTURES.spring)
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.hazard);
      const zone = this.add.zone(s.x + s.w / 2, s.y + s.h / 2, s.w, s.h + 12);
      zone.setData('velocity', s.velocity);
      zone.setData('sprite', sprite);
      zone.setData('top', s.y);
      this.springs.add(zone);
      (zone.body as Phaser.Physics.Arcade.StaticBody).updateFromGameObject();
      objects.push(sprite, zone);
    }
    for (const item of chunk.pickups) {
      const image = this.add
        .image(item.x, item.y, ITEM_TEXTURES[item.kind])
        .setDepth(DEPTH.item) as PickupObject;
      image.pickupKind = item.kind;
      this.pickups.add(image);
      const body = image.body as Phaser.Physics.Arcade.Body;
      body.setCircle(Math.max(image.width, image.height) / 2);
      this.tweens.add({
        targets: image,
        y: item.y - 6,
        duration: 700 + ((index * 37 + item.x) % 300),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      objects.push(image);
    }
    for (const c of chunk.creatures) {
      const texture =
        c.kind === 'beetle'
          ? ITEM_TEXTURES.beetle
          : c.kind === 'bat'
            ? ITEM_TEXTURES.bat
            : ITEM_TEXTURES.rock;
      const y = c.kind === 'beetle' ? c.y - 12 : c.y;
      const creature = this.add.image(c.x, y, texture).setDepth(DEPTH.hazard) as CreatureObject;
      creature.creatureData = c;
      creature.awake = false;
      creature.dir = -1;
      this.creatures.add(creature);
      const body = creature.body as Phaser.Physics.Arcade.Body;
      if (c.kind === 'beetle') {
        body.setSize(30, 20, true);
        body.setAllowGravity(true);
      } else if (c.kind === 'bat') {
        body.setCircle(12, 8, 0);
        body.setAllowGravity(false);
      } else {
        body.setCircle(16, 4, 2);
        body.setAllowGravity(false);
        const groundTop = chunk.platforms.find(
          (p) => (p.kind === 'ground' || p.kind === 'ice') && p.x <= c.x && c.x <= p.x + p.w,
        )?.y;
        if (groundTop !== undefined) {
          creature.marker = this.add
            .image(c.x, groundTop - 6, ITEM_TEXTURES.dust)
            .setScale(4, 1.2)
            .setAlpha(0.35)
            .setTint(0x333333)
            .setDepth(DEPTH.ground + 1);
          objects.push(creature.marker);
        }
      }
      body.moves = false; // asleep until Georgi comes near
      objects.push(creature);
    }
    for (const z of chunk.zones) {
      const zone = this.add.zone(z.x + z.w / 2, z.y + z.h / 2, z.w, z.h);
      zone.setData('force', z.force);
      this.windZones.add(zone);
      (zone.body as Phaser.Physics.Arcade.StaticBody).updateFromGameObject();
      const streaks = this.add
        .tileSprite(z.x + z.w / 2, z.y + z.h / 2, z.w, z.h, ITEM_TEXTURES.wind)
        .setAlpha(0.6)
        .setDepth(DEPTH.item - 1);
      objects.push(zone, streaks);
      this.tweens.add({
        targets: streaks,
        tilePositionX: { from: 0, to: Math.sign(z.force) * -256 },
        duration: 1400,
        repeat: -1,
      });
    }
    for (const r of chunk.rollers) {
      const roller = this.add
        .image(r.x, r.y - r.radius, ITEM_TEXTURES.roller)
        .setDepth(DEPTH.hazard) as RollerObject;
      roller.rollerData = r;
      roller.awake = false;
      this.rollers.add(roller);
      const body = roller.body as Phaser.Physics.Arcade.Body;
      body.setCircle(r.radius);
      body.setBounce(0, 0);
      body.moves = false; // asleep until Georgi comes near
      objects.push(roller);
    }
    for (const f of chunk.flags) {
      const flag = this.add
        .image(f.x, f.y, ITEM_TEXTURES.flag)
        .setOrigin(0.15, 1)
        .setDepth(DEPTH.flag);
      const label = this.add
        .text(f.x, f.y - 74, `LEVEL ${f.levelIndex + 1} · ${f.name}`, {
          fontFamily: HUD_FONT,
          fontSize: '15px',
          color: '#ffffff',
          stroke: '#1d2a36',
          strokeThickness: 4,
        })
        .setOrigin(0, 1)
        .setDepth(DEPTH.flag);
      objects.push(flag, label);
    }
    this.live.set(index, { chunk, objects });
    this.updateBounds();
  }

  private updateMovers(): void {
    const t = this.elapsedMs;
    for (const m of this.movers) {
      const motion = m.platform.motion;
      if (!motion || !m.sprite.active) continue;
      const rest =
        motion.axis === 'x' ? m.platform.x + m.platform.w / 2 : m.platform.y + m.platform.h / 2;
      const value =
        rest + motion.amplitude * Math.sin(2 * Math.PI * (t / motion.periodMs + motion.phase));
      m.lastX = m.sprite.x;
      m.lastY = m.sprite.y;
      if (motion.axis === 'x') m.sprite.x = value;
      else m.sprite.y = value;
    }
  }

  private updateRollers(): void {
    const px = this.player.x;
    for (const obj of this.rollers.getChildren()) {
      const roller = obj as RollerObject;
      if (!roller.active) continue;
      const data = roller.rollerData;
      if (!data) continue;
      const body = roller.body as Phaser.Physics.Arcade.Body;
      if (!roller.awake && roller.x - px < ROLLER_WAKE_PX) {
        roller.awake = true;
        body.moves = true;
        body.setVelocityX(-data.speed);
        body.setAngularVelocity(-(data.speed / data.radius) * 57.3);
      }
      if (roller.awake && body.velocity.x === 0 && body.blocked.left) {
        // Stuck against a terrace wall: keep spinning in place, it stays a hazard.
        body.setAngularVelocity(-120);
      }
      if (roller.x < px - ROLLER_WAKE_PX || roller.y > WORLD_H + 200) roller.destroy();
    }
  }

  private updateCreatures(): void {
    const px = this.player.x;
    const t = this.elapsedMs;
    for (const obj of this.creatures.getChildren()) {
      const c = obj as CreatureObject;
      if (!c.active) continue;
      const data = c.creatureData;
      if (!data) continue;
      const body = c.body as Phaser.Physics.Arcade.Body;
      if (!c.awake && c.x - px < ROLLER_WAKE_PX) {
        c.awake = true;
        body.moves = true;
        if (data.kind === 'beetle') body.setVelocityX(-data.speed);
      }
      if (!c.awake) continue;
      if (data.kind === 'beetle') {
        const platform = this.platformUnder(data);
        const halfW = 15;
        const left = platform ? platform.x + halfW : c.x - 1;
        const right = platform ? platform.x + platform.w - halfW : c.x + 1;
        if ((c.x <= left && c.dir === -1) || body.blocked.left) c.dir = 1;
        else if ((c.x >= right && c.dir === 1) || body.blocked.right) c.dir = -1;
        body.setVelocityX((c.dir ?? -1) * data.speed);
        c.setFlipX(c.dir === 1);
      } else if (data.kind === 'bat') {
        c.y = data.y + data.range * Math.sin((2 * Math.PI * t) / data.speed);
        c.setScale(1, 0.75 + 0.25 * Math.sin(t / 60));
      } else if (data.kind === 'rock') {
        if (!c.falling && Math.abs(px - c.x) < data.range) {
          c.falling = true;
          body.setAllowGravity(true);
          body.setVelocityY(60);
          this.tweens.add({ targets: c, angle: 25, duration: 500 });
        }
        if (c.marker) c.marker.setAlpha(c.falling ? 0.7 : 0.25 + 0.15 * Math.sin(t / 120));
      }
      if (c.x < px - ROLLER_WAKE_PX || c.y > WORLD_H + 200) {
        c.marker?.destroy();
        c.destroy();
      }
    }
  }

  private platformUnder(c: GenCreature): GenPlatform | null {
    for (const live of this.live.values()) {
      if (c.x < live.chunk.x0 || c.x > live.chunk.x1) continue;
      const p = live.chunk.platforms.find(
        (pl) => pl.y === c.y && pl.x <= c.x && c.x <= pl.x + pl.w,
      );
      if (p) return p;
    }
    return null;
  }

  private creatureLanded(creature: CreatureObject): void {
    const data = creature.creatureData;
    if (data?.kind !== 'rock' || !creature.falling || !creature.active) return;
    const body = creature.body as Phaser.Physics.Arcade.Body;
    if (!body.blocked.down) return;
    this.dust.explode(12, creature.x, creature.y + 14);
    this.cameras.main.shake(70, 0.003);
    creature.marker?.destroy();
    creature.destroy();
  }

  private touchCreature(creature: CreatureObject): void {
    if (!creature.active || this.mode !== 'running') return;
    const data = creature.creatureData;
    if (!data) return;
    const body = this.player.body;
    const stompable = data.kind !== 'rock';
    const stomped = stompable && body.velocity.y > 0 && this.player.feetY < creature.y - 2;
    if (stomped) {
      creature.marker?.destroy();
      creature.destroy();
      body.setVelocityY(-STOMP_BOUNCE);
      const before = this.score.multiplier;
      this.score = stomp(this.score);
      this.sfx.play('stomp');
      this.vibrate(15);
      this.dust.explode(10, creature.x, creature.y);
      if (this.score.multiplier > before) this.punch(this.hud.combo);
      return;
    }
    if (data.kind === 'rock' && !creature.falling) return; // a hanging rock is harmless
    this.hit(data.kind);
  }

  private vibrate(ms: number): void {
    const nav = navigator as Navigator & { vibrate?: (pattern: number) => boolean };
    if (typeof nav.vibrate === 'function') {
      try {
        nav.vibrate(ms);
      } catch {
        /* not supported */
      }
    }
  }

  private touchRoller(roller: RollerObject): void {
    if (!roller.active || this.mode !== 'running') return;
    const body = this.player.body;
    const stomped = body.velocity.y > 0 && this.player.feetY < roller.y - 4;
    if (stomped) {
      roller.destroy();
      body.setVelocityY(-STOMP_BOUNCE);
      const before = this.score.multiplier;
      this.score = stomp(this.score);
      this.sfx.play('stomp');
      this.vibrate(15);
      this.dust.explode(10, roller.x, roller.y);
      if (this.score.multiplier > before) this.punch(this.hud.combo);
      return;
    }
    this.hit('roller');
  }

  // ---------------------------------------------------------------- contacts

  private touchPlatform(zone: Zone): void {
    const p = zone.platformData;
    if (!p || !this.player.body.touching.down) return;
    this.groundedPlatform = p;
    if (p.kind === 'ground' && this.player.x > p.x + 40 && this.player.x < p.x + p.w - 40) {
      this.safeSpot = { x: this.player.x, y: p.y };
    }
  }

  private touchCrumble(zone: Zone): void {
    if (!this.player.body.touching.down || zone.crumbling) return;
    zone.crumbling = true;
    this.groundedPlatform = zone.platformData ?? null;
    const sprite = zone.getData('sprite') as Phaser.GameObjects.TileSprite;
    this.tweens.add({ targets: sprite, x: sprite.x + 3, duration: 40, yoyo: true, repeat: 6 });
    this.time.delayedCall(CRUMBLE_DELAY_MS, () => {
      if (!zone.active) return;
      (zone.body as Phaser.Physics.Arcade.StaticBody).enable = false;
      this.tweens.add({
        targets: sprite,
        y: sprite.y + 320,
        alpha: 0,
        duration: 700,
        ease: 'Quad.easeIn',
      });
    });
  }

  private touchMover(sprite: Phaser.GameObjects.TileSprite): void {
    if (!this.player.body.touching.down) return;
    const mover = this.movers.find((m) => m.sprite === sprite);
    if (!mover) return;
    this.groundedPlatform = mover.platform;
    // Carry: follow the platform's travel this frame.
    const dx = mover.sprite.x - mover.lastX;
    if (dx !== 0) this.player.sprite.x += dx;
  }

  private touchSpring(zone: Phaser.GameObjects.Zone): void {
    if (this.mode !== 'running') return;
    const body = this.player.body;
    const top = zone.getData('top') as number;
    // Launch when landing on the pad or stepping onto it, never while already rising.
    if (body.velocity.y < 0 || this.player.feetY > top + 22) return;
    const velocity = zone.getData('velocity') as number;
    body.setVelocityY(-velocity);
    this.sfx.play('spring');
    const sprite = zone.getData('sprite') as Phaser.GameObjects.Image;
    this.tweens.add({ targets: sprite, scaleY: 0.5, duration: 70, yoyo: true });
  }

  private collectPickup(item: PickupObject): void {
    if (!item.active || this.mode !== 'running') return;
    const kind = item.pickupKind ?? 'bone';
    item.destroy();
    if (kind === 'bone' || kind === 'golden') {
      const before = this.score.multiplier;
      this.score = collect(this.score, kind);
      this.sfx.play(kind);
      if (this.score.multiplier > before) this.punch(this.hud.combo);
      return;
    }
    this.powerups = activate(this.powerups, kind as PowerupKind);
    this.sfx.play('powerup');
    this.banner(`${POWERUPS[kind as PowerupKind].label}!`, '', 900);
  }

  private applyMagnet(dt: number): void {
    const radius = magnetRadiusPx(this.powerups);
    if (radius <= 0) return;
    const px = this.player.x;
    const py = this.player.feetY - 50;
    const step = (POWERUPS.magnet.pullPxPerS * dt) / 1000;
    for (const obj of this.pickups.getChildren()) {
      const item = obj as PickupObject;
      if (!item.active || (item.pickupKind !== 'bone' && item.pickupKind !== 'golden')) continue;
      const dx = px - item.x;
      const dy = py - item.y;
      const dist = Math.hypot(dx, dy);
      if (dist > radius || dist < 1) continue;
      const k = Math.min(1, step / dist);
      item.x += dx * k;
      item.y += dy * k;
    }
  }

  private hit(reason: HitReason): void {
    if (this.mode !== 'running' || this.elapsedMs < this.invulnerableUntil) return;
    if (reason !== 'pit') {
      const { state, absorbed } = absorbHit(this.powerups);
      this.powerups = state;
      if (absorbed) {
        this.sfx.play('shield');
        this.invulnerableUntil = this.elapsedMs + INVULNERABLE_MS;
        this.banner('shield!', 'absorbed the hit', 700);
        return;
      }
    }
    this.score = loseLife(this.score);
    this.recordDeath(reason);
    this.sfx.play('hit');
    this.vibrate(40);
    this.cameras.main.flash(180, 255, 80, 80);
    if (isGameOver(this.score)) {
      this.gameOver();
      return;
    }
    this.respawns += 1;
    this.player.respawn({ x: this.safeSpot.x, feetY: this.safeSpot.y, facing: 'right' });
    this.powerups = landed(this.powerups);
    this.invulnerableUntil = this.elapsedMs + INVULNERABLE_MS;
    this.snapCamera();
  }

  private recordDeath(reason: HitReason): void {
    const pattern = this.chunkAt(this.player.x)?.pattern ?? 'unknown';
    this.deaths = {
      byReason: { ...this.deaths.byReason, [reason]: (this.deaths.byReason[reason] ?? 0) + 1 },
      byPattern: {
        ...this.deaths.byPattern,
        [pattern]: (this.deaths.byPattern[pattern] ?? 0) + 1,
      },
    };
    this.saveStats();
  }

  private chunkAt(x: number): Chunk | null {
    for (const live of this.live.values())
      if (x >= live.chunk.x0 && x < live.chunk.x1) return live.chunk;
    return null;
  }

  private checkLevel(): void {
    const index = levelIndexAt(this.player.x);
    if (index <= this.levelIndex) return;
    const completed = this.levelIndex;
    this.levelIndex = index;
    this.score = completeLevel(this.score, completed);
    const level = levelByIndex(index);
    this.sfx.play('levelUp');
    this.banner(
      `LEVEL ${index + 1} · ${level.name}`,
      `${level.subtitle} · +${SCORING.levelBonus * (completed + 1)} · +1 life`,
      2200,
    );
    this.swapSky(level);
  }

  private swapSky(level: LevelDef): void {
    ensureThemeTextures(this, level.theme, GAME_HEIGHT);
    const key = themeKeys(level.theme).sky;
    if (this.skyNext) this.skyNext.destroy();
    const next = this.add
      .tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, key)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(DEPTH.sky + 1)
      .setAlpha(0);
    this.skyNext = next;
    this.tweens.add({
      targets: next,
      alpha: 1,
      duration: 1200,
      onComplete: () => {
        this.sky.setTexture(key);
        const keys = themeKeys(level.theme);
        this.hills.setTexture(keys.hills);
        this.clouds.setTexture(keys.clouds);
        next.destroy();
        if (this.skyNext === next) this.skyNext = null;
      },
    });
    this.cameras.main.setBackgroundColor(level.theme.sky[1]);
  }

  // --------------------------------------------------------------------- hud

  private buildHud(): void {
    const pad = 16;
    const text = (x: number, y: number, size: number, color = '#ffffff') =>
      this.add
        .text(x, y, '', {
          fontFamily: HUD_FONT,
          fontSize: `${size}px`,
          fontStyle: 'bold',
          color,
          stroke: '#1d2a36',
          strokeThickness: Math.max(3, Math.round(size / 6)),
        })
        .setScrollFactor(0)
        .setDepth(DEPTH.hud);
    const hearts: Phaser.GameObjects.Image[] = [];
    for (let i = 0; i < SCORING.maxLives; i++)
      hearts.push(
        this.add
          .image(GAME_WIDTH - pad - 14 - i * 28, pad + 16, ITEM_TEXTURES.heart)
          .setScrollFactor(0)
          .setDepth(DEPTH.hud),
      );
    this.hud = {
      score: text(pad, pad, 34),
      combo: text(pad, pad + 42, 20, '#ffd23f'),
      level: text(GAME_WIDTH / 2, pad, 20).setOrigin(0.5, 0),
      distance: text(GAME_WIDTH / 2, pad + 30, 26).setOrigin(0.5, 0),
      hearts,
      best: text(GAME_WIDTH - pad, pad + 40, 15).setOrigin(1, 0),
      powerups: text(pad, GAME_HEIGHT - pad, 16, '#dff7ff').setOrigin(0, 1),
      help: this.add
        .text(GAME_WIDTH - pad, GAME_HEIGHT - pad, HELP_TEXT, {
          fontFamily: 'monospace',
          fontSize: '13px',
          color: '#1d2a36',
          backgroundColor: '#ffffffb0',
          padding: { x: 8, y: 5 },
          align: 'right',
        })
        .setOrigin(1, 1)
        .setScrollFactor(0)
        .setDepth(DEPTH.hud),
      banner: text(GAME_WIDTH / 2, GAME_HEIGHT * 0.3, 44, '#ffd23f')
        .setOrigin(0.5)
        .setAlpha(0),
      bannerSub: text(GAME_WIDTH / 2, GAME_HEIGHT * 0.3 + 44, 18)
        .setOrigin(0.5)
        .setAlpha(0),
      overlayText: this.add
        .text(pad, 110, '', {
          fontFamily: 'monospace',
          fontSize: '13px',
          color: '#e8ffe8',
          backgroundColor: '#0a1a10d0',
          padding: { x: 8, y: 6 },
        })
        .setScrollFactor(0)
        .setDepth(DEPTH.hud)
        .setVisible(false),
    };
    this.updateHud();
  }

  private updateHud(): void {
    const h = this.hud;
    h.score.setText(`${this.score.score}`);
    h.combo.setText(
      this.score.combo > 1 ? `×${this.score.multiplier}  combo ${this.score.combo}` : '',
    );
    const level = levelByIndex(this.levelIndex);
    h.level.setText(
      `${this.daily ? `DAILY ${dailyLabel(this.seed)} · ` : ''}LEVEL ${this.levelIndex + 1} · ${level.name}`,
    );
    h.distance.setText(`${distanceM(this.score)} m`);
    h.hearts.forEach((heart, i) => heart.setVisible(i < this.score.lives));
    h.best.setText(this.best ? `best ${this.best.score}` : '');
    h.powerups.setText(
      activeList(this.powerups)
        .map((p) => `${POWERUPS[p.kind].label} ${p.secondsLeft}s`)
        .join('   '),
    );
  }

  private banner(title: string, sub: string, ms: number): void {
    const { banner, bannerSub } = this.hud;
    this.tweens.killTweensOf([banner, bannerSub]);
    banner.setText(title).setAlpha(0).setScale(0.8);
    bannerSub.setText(sub).setAlpha(0);
    this.tweens.add({ targets: banner, alpha: 1, scale: 1, duration: 220, ease: 'Back.easeOut' });
    this.tweens.add({ targets: bannerSub, alpha: 1, duration: 300, delay: 120 });
    this.tweens.add({ targets: [banner, bannerSub], alpha: 0, duration: 400, delay: ms });
  }

  private punch(target: Phaser.GameObjects.Text): void {
    this.tweens.killTweensOf(target);
    target.setScale(1);
    this.tweens.add({
      targets: target,
      scale: 1.35,
      duration: 110,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  private showScreen(title: string, subtitle: string, lines: string[]): void {
    this.hideScreen();
    const bg = this.add.rectangle(0, 0, 760, 360, 0x102033, 0.86).setStrokeStyle(3, 0xffd23f, 1);
    const t = this.add
      .text(0, -120, title, {
        fontFamily: HUD_FONT,
        fontSize: '54px',
        fontStyle: 'bold',
        color: '#ffd23f',
        stroke: '#5a3a12',
        strokeThickness: 8,
      })
      .setOrigin(0.5);
    const s = this.add
      .text(0, -68, subtitle, { fontFamily: HUD_FONT, fontSize: '22px', color: '#ffffff' })
      .setOrigin(0.5);
    const body = this.add
      .text(0, 30, lines.join('\n'), {
        fontFamily: HUD_FONT,
        fontSize: '18px',
        color: '#e8eef5',
        align: 'center',
        lineSpacing: 6,
      })
      .setOrigin(0.5);
    this.screen = this.add
      .container(GAME_WIDTH / 2, GAME_HEIGHT * 0.45, [bg, t, s, body])
      .setScrollFactor(0)
      .setDepth(DEPTH.overlay);
  }

  private hideScreen(): void {
    this.screen?.destroy();
    this.screen = null;
  }

  private setOverlay(on: boolean): void {
    this.overlayVisible = on;
    this.hud.overlayText.setVisible(on);
    snapshot.overlay = on;
  }

  private setHelp(on: boolean): void {
    this.helpVisible = on;
    this.hud.help.setVisible(on);
    snapshot.help = on;
  }

  private toGallery(): void {
    this.controls?.release();
    this.scene.start('Gallery', {
      manifest: this.manifest,
      tierLabel: this.tierLabel,
      registered: this.registered,
    });
  }

  // --------------------------------------------------------------- snapshot

  private publish(): void {
    const result = this.player.result;
    const body = this.player.body;
    const machine = this.player.machine;
    const pose = result?.pose ?? machine.pose;
    const round = (v: number) => Math.round(v * 10) / 10;
    snapshot.player = {
      state: machine.state,
      facing: machine.facing,
      action: pose.action,
      frame: pose.frame,
      phase: pose.phase === null ? null : Math.round(pose.phase * 1000) / 1000,
      queue: [...pose.queue],
      x: round(this.player.x),
      feetY: round(this.player.feetY),
      vx: Math.round(body.velocity.x),
      vy: Math.round(body.velocity.y),
      grounded: this.player.grounded,
      coyoteMs: Math.round(machine.coyoteLeftMs),
      jumpBufferMs: Math.round(machine.jumpBufferLeftMs),
      body: { x: round(body.x), y: round(body.y), w: round(body.width), h: round(body.height) },
      bodyFamily: this.player.bodyFamily,
      blend: { next: pose.blend.nextFrame, fraction: Math.round(pose.blend.fraction * 100) / 100 },
      frameBlend: this.player.frameBlend,
    };
    snapshot.facing = machine.facing;
    pushHistory(snapshot.stateHistory, machine.state);
    pushHistory(snapshot.actionHistory, pose.action);
    const cam = this.cameras.main;
    snapshot.camera = { scrollX: round(cam.scrollX), scrollY: round(cam.scrollY) };
    snapshot.endless = {
      mode: this.mode,
      seed: this.seed,
      score: this.score.score,
      distanceM: distanceM(this.score),
      bones: this.score.bones,
      combo: this.score.combo,
      multiplier: this.score.multiplier,
      lives: this.score.lives,
      level: { index: this.levelIndex, name: levelByIndex(this.levelIndex).name },
      powerups: activeList(this.powerups).map((p) => p.kind),
      chunks: this.nextChunk,
      liveChunks: this.live.size,
      rollers: this.rollers.countActive(true),
      creatures: this.creatures.countActive(true),
      wind: this.windThisFrame,
      onIce: this.groundedPlatform?.kind === 'ice',
      deaths: this.deaths,
      respawns: this.respawns,
      safeSpot: { ...this.safeSpot },
      best: this.best,
      muted: this.sfx.muted,
      groundedOn: this.groundedPlatform?.kind ?? null,
      touch: this.touch !== null,
      daily: this.daily,
    };
  }

  private drawOverlay(): void {
    const g = this.debugGraphics;
    g.clear();
    if (!this.overlayVisible) return;
    const p = snapshot.player;
    const e = snapshot.endless;
    if (!p || !e) return;
    const info = this.graph.actions[this.player.machine.pose.action];
    this.hud.overlayText.setText(
      [
        `mode     ${e.mode}  seed ${e.seed}  chunks ${e.chunks} (live ${e.liveChunks})  level ${e.level.index + 1} ${e.level.name}`,
        `state    ${p.state}  facing ${p.facing}  on ${e.groundedOn ?? '-'}  body ${p.bodyFamily}  blend ${p.frameBlend ? `${p.blend.fraction.toFixed(2)}→#${p.blend.next}` : 'off'}`,
        `action   ${p.action} #${p.frame}/${info.frames - 1}  phase ${p.phase === null ? 'loop' : p.phase.toFixed(2)}  queue ${p.queue.join(' → ') || '-'}`,
        `velocity ${p.vx}, ${p.vy}   grounded ${p.grounded}   coyote ${p.coyoteMs}  buffer ${p.jumpBufferMs}`,
        `feet     ${p.x}, ${p.feetY}   body ${p.body.w}×${p.body.h} @ ${p.body.x}, ${p.body.y}   safe ${Math.round(e.safeSpot.x)}, ${e.safeSpot.y}`,
        `score    ${e.score}  ${e.distanceM} m  bones ${e.bones}  combo ${e.combo} ×${e.multiplier}  lives ${e.lives}  respawns ${e.respawns}`,
        `powerups ${e.powerups.join(', ') || '-'}   fps ${Math.round(this.game.loop.actualFps)}   tier ${this.tierLabel}`,
      ].join('\n'),
    );
    const view = this.cameras.main.worldView;
    for (const live of this.live.values()) {
      for (const plat of live.chunk.platforms) {
        if (plat.x + plat.w < view.x || plat.x > view.right) continue;
        g.lineStyle(
          2,
          plat.kind === 'moving' ? 0x33ccff : plat.kind === 'crumble' ? 0xff9933 : 0x2ecc71,
          0.8,
        );
        g.strokeRect(plat.x, plat.y, plat.w, plat.h);
        if (plat.kind === 'one-way' || plat.kind === 'crumble') {
          g.lineStyle(3, 0xff33ff, 1);
          g.lineBetween(plat.x, plat.y, plat.x + plat.w, plat.y);
        }
      }
      for (const h of live.chunk.hazards) {
        g.lineStyle(2, 0xff3355, 0.9);
        g.strokeRect(h.x, h.y, h.w, h.h);
      }
    }
    for (const obj of this.rollers.getChildren()) {
      const roller = obj as RollerObject;
      if (!roller.active) continue;
      g.lineStyle(2, roller.awake ? 0xff3355 : 0xff9933, 0.9);
      g.strokeCircle(roller.x, roller.y, roller.rollerData?.radius ?? 22);
    }
    for (const obj of this.creatures.getChildren()) {
      const c = obj as CreatureObject;
      if (!c.active) continue;
      const b = c.body as Phaser.Physics.Arcade.Body;
      g.lineStyle(2, c.awake ? 0xff3355 : 0xff9933, 0.9);
      g.strokeRect(b.x, b.y, b.width, b.height);
    }
    for (const live of this.live.values())
      for (const z of live.chunk.zones) {
        g.lineStyle(1, 0x33ccff, 0.8);
        g.strokeRect(z.x, z.y, z.w, z.h);
      }
    g.lineStyle(2, 0xffcc00, 0.95);
    g.strokeRect(p.body.x, p.body.y, p.body.w, p.body.h);
    // The current frame's opaque pixels (blue): what the bodies are authored against.
    const v = this.player.visualBounds;
    g.lineStyle(1, 0x3399ff, 0.8);
    g.strokeRect(v.x, v.y, v.w, v.h);
    g.lineStyle(2, 0xff3355, 1);
    g.lineBetween(p.x - 12, p.feetY, p.x + 12, p.feetY);
    g.lineBetween(p.x, p.feetY - 12, p.x, p.feetY + 12);
    g.lineStyle(2, 0x00e5ff, 0.9);
    g.strokeCircle(e.safeSpot.x, e.safeSpot.y, 8);
  }

  // ------------------------------------------------------------- persistence

  private loadBest(): BestRecord | null {
    try {
      const raw = window.localStorage.getItem(BEST_KEY);
      return raw ? parseRecord(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }

  private loadStats(): DeathStats {
    try {
      const raw = window.localStorage.getItem(STATS_KEY);
      const parsed = raw ? (JSON.parse(raw) as Partial<DeathStats>) : null;
      return {
        byReason: { ...(parsed?.byReason ?? {}) },
        byPattern: { ...(parsed?.byPattern ?? {}) },
      };
    } catch {
      return { byReason: {}, byPattern: {} };
    }
  }

  private saveStats(): void {
    try {
      window.localStorage.setItem(STATS_KEY, JSON.stringify(this.deaths));
    } catch {
      /* storage unavailable */
    }
  }

  private saveBest(): void {
    try {
      if (this.best) window.localStorage.setItem(BEST_KEY, JSON.stringify(this.best));
    } catch {
      /* storage unavailable: the best is kept for this session only */
    }
  }

  private fail(message: string): void {
    this.failed = true;
    recordError(message);
    console.error(`[georgi] ${message}`);
    this.add
      .text(24, 24, `Georgi endless run failed:\n${message}`, {
        color: '#ff5555',
        fontSize: '18px',
        fontFamily: 'monospace',
        wordWrap: { width: this.scale.width - 48 },
      })
      .setOrigin(0, 0);
  }
}
